/**
 * Inference Stack Architecture Diagram
 *
 * Shows the three layers of the inference stack:
 * Runtime (vLLM, SGLang, TensorRT-LLM) → Forecaster Intelligence Layer → Infrastructure
 * No secret sauce — public-facing architecture positioning only.
 */
export const InferenceStackDiagram = () => {
    return (
        <div className="bg-obsidian-800/50 border border-white/[0.06] rounded-2xl p-6 md:p-10">
            {/* Runtime Layer */}
            <div className="border border-emerald-500/20 rounded-xl p-5 mb-3 bg-emerald-500/[0.03]">
                <div className="text-center mb-4">
                    <span className="text-emerald-400 text-sm font-semibold tracking-wider uppercase">Runtime</span>
                </div>

                {/* Model Performance Techniques */}
                <div className="border border-emerald-500/10 rounded-lg p-4 mb-4 bg-emerald-500/[0.02]">
                    <p className="text-xs text-emerald-400/60 tracking-wider uppercase text-center mb-3">
                        Model Performance Techniques
                    </p>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                        {['Batching', 'Caching', 'Quantization', 'Speculation', 'Parallelism'].map(t => (
                            <div key={t} className="px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-center">
                                <span className="text-sm text-emerald-300">{t}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Serving Frameworks */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                    {['vLLM', 'SGLang', 'TensorRT-LLM'].map(f => (
                        <div key={f} className="px-4 py-2.5 bg-emerald-500/[0.06] border border-emerald-500/15 rounded-lg text-center">
                            <span className="text-sm text-emerald-200 font-medium">{f}</span>
                        </div>
                    ))}
                </div>

                {/* Foundation */}
                <div className="grid grid-cols-2 gap-2">
                    <div className="px-4 py-2 bg-emerald-500/[0.04] border border-emerald-500/10 rounded-lg text-center">
                        <span className="text-sm text-emerald-300/70">PyTorch</span>
                    </div>
                    <div className="px-4 py-2 bg-emerald-500/[0.04] border border-emerald-500/10 rounded-lg text-center">
                        <span className="text-sm text-emerald-300/70">CUDA / ROCm / OneAPI</span>
                    </div>
                </div>
            </div>

            {/* Arrow: Runtime <-> Forecaster */}
            <div className="flex justify-center my-1">
                <div className="flex flex-col items-center">
                    <div className="w-2 h-2 border-l border-t border-blue-400/60 rotate-45 mb-0.5" />
                    <div className="w-px h-4 bg-gradient-to-b from-blue-400/40 to-blue-400/40" />
                    <div className="w-2 h-2 border-r border-b border-blue-400/60 rotate-45 -mt-0.5" />
                </div>
            </div>

            {/* FORECASTER INTELLIGENCE LAYER */}
            <div className="border-2 border-blue-500/40 rounded-xl p-5 mb-3 bg-blue-500/[0.05] relative">
                <div className="flex justify-center mb-4">
                    <div className="px-5 py-1.5 bg-blue-500 rounded-full">
                        <span className="text-white text-sm font-bold tracking-wider uppercase">
                            Forecaster — Intelligence Layer
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                    {[
                        'Predictive Routing',
                        'Predictive Autoscaling',
                        'KV Cache Intelligence',
                        'Fault Prediction',
                    ].map(c => (
                        <div key={c} className="px-3 py-3 bg-blue-500/10 border border-blue-500/25 rounded-lg text-center">
                            <span className="text-sm text-blue-200">{c}</span>
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                        'Cost Arbitrage',
                        'SLO Classification',
                        'Memory Hierarchy',
                        'Energy Optimization',
                    ].map(c => (
                        <div key={c} className="px-3 py-3 bg-blue-500/10 border border-blue-500/25 rounded-lg text-center">
                            <span className="text-sm text-blue-200">{c}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Arrow: Forecaster <-> Infrastructure */}
            <div className="flex justify-center my-1">
                <div className="flex flex-col items-center">
                    <div className="w-2 h-2 border-l border-t border-blue-400/60 rotate-45 mb-0.5" />
                    <div className="w-px h-4 bg-gradient-to-b from-blue-400/40 to-blue-400/40" />
                    <div className="w-2 h-2 border-r border-b border-blue-400/60 rotate-45 -mt-0.5" />
                </div>
            </div>

            {/* Infrastructure Layer */}
            <div className="border border-amber-500/20 rounded-xl p-5 bg-amber-500/[0.03]">
                <div className="text-center mb-4">
                    <span className="text-amber-400 text-sm font-semibold tracking-wider uppercase">Infrastructure</span>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-3">
                    {['Routing', 'Load Balancing', 'Autoscaling'].map(i => (
                        <div key={i} className="px-4 py-2.5 bg-amber-500/[0.08] border border-amber-500/20 rounded-lg text-center">
                            <span className="text-sm text-amber-200">{i}</span>
                        </div>
                    ))}
                </div>

                <div className="px-4 py-2.5 bg-amber-500/[0.06] border border-amber-500/15 rounded-lg text-center mb-3">
                    <span className="text-sm text-amber-200/80">Multi-Cloud Capacity Management</span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                    {['GPUs', 'Storage', 'Networking'].map(i => (
                        <div key={i} className="px-4 py-2 bg-amber-500/[0.04] border border-amber-500/10 rounded-lg text-center">
                            <span className="text-sm text-amber-300/60">{i}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Caption */}
            <p className="text-center text-gray-500 text-sm mt-5">
                Forecaster is the intelligence layer between Runtime and Infrastructure
            </p>
        </div>
    );
};
